/**
 * useLearnStyle — shared TanStack mutation around the "learn a visual style
 * from a reference file" surface (``POST /api/v1/styles/learn``).
 *
 * One hook, two consumers (DRY):
 *   - the ``document_create`` proposal picker host
 *     (``useDocumentCreateStyleTemplate``) — drop a reference, auto-select the
 *     learned style.
 *   - the settings gallery (``MarpTemplateSettings``) — manage learned styles
 *     alongside the curated built-ins.
 *
 * On success the mutation:
 *   - records the returned style's ``id`` as "learned" (see {@link isLearnedStyleId})
 *     so the picker / gallery can group it under "I tuoi stili" — the shared
 *     BE entry shape (``GET /api/v1/styles``) carries no ``is_learned`` flag,
 *     so we keep the provenance client-side;
 *   - invalidates the ``useStyles`` query so the learned style appears in the
 *     list immediately, selectable EXACTLY like a built-in preset
 *     (``content["_style_id"] = <learned id>``).
 */

import * as React from "react";
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { learnStyleFromSample, type StylePreset } from "./api";
import { stylesQueryKey } from "./useStyles";

/**
 * localStorage key holding the set of style ids the user has LEARNED on this
 * device. The BE list endpoint returns learned styles inline with the
 * built-ins (same entry shape, no flag), so we remember which ids are learned
 * to render the "I tuoi stili" group + a manageable badge.
 */
const LEARNED_IDS_KEY = "ando.learnedStyleIds.v1";

function readLearnedIds(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(LEARNED_IDS_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function persistLearnedIds(ids: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LEARNED_IDS_KEY, JSON.stringify([...ids]));
  } catch {
    // Quota / private-mode — provenance is best-effort, never block the flow.
  }
}

/** Record a freshly-learned style id (idempotent). */
export function rememberLearnedStyleId(id: string): void {
  const ids = readLearnedIds();
  if (ids.has(id)) return;
  ids.add(id);
  persistLearnedIds(ids);
}

/** Whether a style id was learned from a reference file on this device. */
export function isLearnedStyleId(id: string): boolean {
  return readLearnedIds().has(id);
}

/** Snapshot of the learned-id set (for ``useMemo`` deps in callers). */
export function getLearnedStyleIds(): ReadonlySet<string> {
  return readLearnedIds();
}

export interface LearnStyleVariables {
  readonly file: File;
  readonly name?: string;
}

export interface UseLearnStyleOptions {
  /** Invoked with the persisted learned style after success (e.g. auto-select). */
  readonly onLearned?: (style: StylePreset) => void;
}

/**
 * Mutation that learns a style from a user-supplied reference file and wires
 * up cache invalidation + learned-id provenance. Callers own the file-pick UX
 * and may pass ``onLearned`` to react (auto-select in the picker, toast in
 * settings).
 */
export function useLearnStyle(
  opts?: UseLearnStyleOptions,
): UseMutationResult<StylePreset, Error, LearnStyleVariables> {
  const queryClient = useQueryClient();
  const onLearned = opts?.onLearned;

  return useMutation<StylePreset, Error, LearnStyleVariables>({
    mutationFn: ({ file, name }) => learnStyleFromSample(file, name),
    onSuccess: (style) => {
      rememberLearnedStyleId(style.id);
      void queryClient.invalidateQueries({ queryKey: stylesQueryKey() });
      onLearned?.(style);
    },
  });
}

/** Accept-list of reference extensions the BE can learn a style from. */
export const LEARN_STYLE_ACCEPT = ".pptx,.docx,.xlsx" as const;
const LEARN_STYLE_EXT = /\.(pptx|docx|xlsx)$/i;
const LEARN_STYLE_MAX_BYTES = 25 * 1024 * 1024; // 25 MB — matches OOXML upload.

/**
 * Validate a candidate reference file before handing it to {@link useLearnStyle}.
 * Returns an Italian error message, or ``null`` when the file is acceptable.
 * Shared by the picker dropzone + the settings upload control (DRY).
 */
export function validateLearnStyleFile(file: File): string | null {
  if (!LEARN_STYLE_EXT.test(file.name)) {
    return "Formato non supportato — carica .pptx / .docx / .xlsx";
  }
  if (file.size > LEARN_STYLE_MAX_BYTES) {
    return `File troppo grande (${(file.size / 1024 / 1024).toFixed(1)} MB · max 25 MB)`;
  }
  return null;
}

/**
 * Tiny controller bundling the file-pick + validation + mutation wiring used
 * by BOTH the picker dropzone and the settings upload control, so neither
 * re-implements drag-state / validation / error plumbing.
 */
export interface LearnStyleController {
  readonly pending: boolean;
  readonly error: string | null;
  readonly clearError: () => void;
  /** Validate then learn; surfaces validation errors locally. */
  readonly learn: (file: File) => void;
}

export function useLearnStyleController(
  opts?: UseLearnStyleOptions,
): LearnStyleController {
  const [error, setError] = React.useState<string | null>(null);
  const mutation = useLearnStyle({
    onLearned: (style) => {
      setError(null);
      opts?.onLearned?.(style);
    },
  });

  const learn = React.useCallback(
    (file: File) => {
      const validationError = validateLearnStyleFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);
      mutation.mutate(
        { file },
        {
          onError: (err) =>
            setError(err instanceof Error ? err.message : "Errore sconosciuto"),
        },
      );
    },
    [mutation],
  );

  return {
    pending: mutation.isPending,
    error,
    clearError: () => setError(null),
    learn,
  };
}
